import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { SaveCustomerNoteInput } from '../../services/CustomerNotes/SaveCustomerNote/SaveCustomerNoteInput';
import { CustomerNote } from '../../models/CustomerNote';
import { Button } from '../../common-components/Button/Button';
import { CardCustomerNote } from './CardCustomerNote/CardCustomerNote';
import { FormCustomerNote, FormCustomerNoteValues } from './FormCustomerNote/FormCustomerNote';
import { FooterContent } from './styles';

interface IModalCustomerNotesProps {
    visible: boolean;
    close: (changed?: boolean) => void;
    title: string;
    customerId?: number;
    notList?: boolean;
}

const ModalCustomerNotes: React.FC<IModalCustomerNotesProps> = ({
    visible,
    close,
    title,
    customerId,
    notList
}) => {

    const { session } = useAuth();
    const [ notes, setNotes ] = React.useState<CustomerNote[]>([]);
    const [ formVisible, setFormVisible ] = React.useState<boolean>(false);
    const [ formValues, setFormValues ] = React.useState<FormCustomerNoteValues|undefined>(undefined);

    const {
        getCustomerNotesByCustomerService,
        saveCustomerNoteService,
        toastService
    } = React.useContext(ServicesContext);

    React.useEffect(() => {
        if (!visible) return;
        getNotes();
    }, [ visible, notList ]);

    const body = React.useMemo(() => {
        if (!visible) return null;

        if (formVisible || notList) {
            return (
                <FormCustomerNote
                    values={formValues}
                    change={(values) => setFormValues(values)}
                    notLabel={notList}
                />
            );
        }

        const orderHasAnyNotes = notes?.length > 0;

        if (!orderHasAnyNotes) {
            return (
                <Alert variant='warning'>
                    {strings.NoneNoteFound}
                </Alert>
            );
        }

        return notes.map(note => {
            return (
                <CardCustomerNote key={`customer-note-${note.id}`} note={note} />
            )
        });
    }, [ visible, formVisible, notes, formValues, notList ]);

    const footer = React.useMemo(() => {
        if (!visible) return null;

        if (formVisible || notList) {
            return (
                <>
                    <Button text={strings.Cancel} width='150px' marginRight='20px' click={() => cancelForm()} />
                    <Button text={strings.Confirm} width='150px' click={() => confirmNote()} />
                </>
            );
        }

        return (
            <>
                <Button text={strings.New} width='150px' marginRight='20px' click={() => insertNote()} />
                <Button text={strings.Update} width='150px' click={() => getNotes()} />
            </>
        );
    }, [ visible, formVisible, notes, formValues, notList ]);

    if (!visible) return null;

    return (
        <Modal
            show={visible}
            onHide={() => clickCloseModal()}
            backdrop="static"
            keyboard={false}
            size="lg"
        >
            <Modal.Header closeButton style={{padding:'10px 1rem', height:'50px'}}>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body id='ModalCustomerNotesBody' style={{maxHeight:'78vh',overflowY:'auto'}}>
                {body}
            </Modal.Body>

            <Modal.Footer>
                <FooterContent>
                    {footer}
                </FooterContent>
            </Modal.Footer>
        </Modal>
    );

    function getNotes() {
        if (!customerId || notList) return;
        getCustomerNotesByCustomerService
            .handle({customerId: customerId}).then(result => {
                setNotes(result?.value?.customerNotes || []);
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongGettingTheData);
            });
    }

    function clickCloseModal(changed?: boolean) {
        setFormVisible(false);
        setFormValues(undefined);
        close(changed);
    }

    function insertNote() {
        setFormValues(undefined);
        setFormVisible(true);
    }

    function cancelForm() {
        setFormVisible(false);
        setFormValues(undefined);
        if (notList) {
            clickCloseModal();
        }
    }

    function confirmNote() {
        if (!customerId) return;

        if (!formValues?.value) {
            toastService.warning(strings.InformTheNote);
            return;
        }

        const input = new SaveCustomerNoteInput();
        input.customerId = customerId;
        input.value = formValues.value;
        input.createdBy = session?.user?.id;
        saveCustomerNoteService.handle(input)
            .then(result => {
                if (notList) {
                    setFormValues(undefined);
                    clickCloseModal(true);
                    return;
                }
                cancelForm();
                getNotes();
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongSendTheData);
            });
    }
}

export {ModalCustomerNotes};